import { useEffect, useRef, useState } from "react";
import { API } from "@/lib/api";

/**
 * useDispatchStream — subscribes to the dispatcher SSE feed and triggers a callback on each event.
 * Falls back to a slow background fetch if EventSource cannot be used.
 *
 * @param onEvent   (eventType, payload) callback for each event
 * @param enabled   boolean — if false, no stream is opened
 */
export default function useDispatchStream(onEvent, enabled = true) {
  const [connected, setConnected] = useState(false);
  const onEventRef = useRef(onEvent);

  // Keep latest callback in a ref so the EventSource doesn't reconnect on every render
  useEffect(() => { onEventRef.current = onEvent; }, [onEvent]);

  useEffect(() => {
    if (!enabled) return undefined;
    const since = new Date().toISOString();
    // EventSource auto-includes session cookies (withCredentials) on same-origin
    // Our backend reads cookie -> resolves dispatcher session
    const url = `${API}/dispatch/stream?since=${encodeURIComponent(since)}`;
    let es;
    try {
      es = new EventSource(url, { withCredentials: true });
    } catch (e) {
      return undefined;
    }
    es.onopen = () => setConnected(true);
    es.onerror = () => setConnected(false);
    const handler = (type) => (msg) => {
      try {
        const data = msg.data ? JSON.parse(msg.data) : {};
        onEventRef.current?.(type, data);
      } catch { /* noop */ }
    };
    es.addEventListener("new_booking", handler("new_booking"));
    es.addEventListener("status_change", handler("status_change"));
    es.addEventListener("notice", handler("notice"));
    return () => { es.close(); };
  }, [enabled]);

  return { connected };
}

/**
 * Triggers a native browser notification if the user has granted permission.
 * Returns silently if permission is not granted (does not prompt automatically).
 */
export function pushBrowserNotification(title, body, tag) {
  if (typeof window === "undefined" || !("Notification" in window)) return;
  if (Notification.permission !== "granted") return;
  try {
    new Notification(title, { body, tag, icon: "/favicon.ico" });
  } catch { /* noop */ }
}

/**
 * Asks the user for notification permission (idempotent — only prompts once).
 * Returns the permission string.
 */
export async function requestBrowserNotificationPermission() {
  if (typeof window === "undefined" || !("Notification" in window)) return "denied";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  try {
    const p = await Notification.requestPermission();
    return p;
  } catch {
    return "denied";
  }
}
